import { describeAction } from '@/app/actions/image/describe';
import { transcribeAction } from '@/app/actions/speech/transcribe';
import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import { handleError } from '@/lib/error/handle';
import { uploadFile } from '@/lib/upload';
import { useAuth, useUser } from '@clerk/nextjs';
import { useProject } from '@/providers/project';
import { useReactFlow, useStore } from '@xyflow/react';
import { Loader2Icon, Play as PlayIcon, Package, FolderOpen, Upload, ImagePlus, Film, ClipboardPaste, Images, Eye, UserSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImagePreviewDialog } from '@/components/nodes/image/preview-dialog';
import { VideoPreviewDialog } from '@/components/nodes/video/preview-dialog';
import { NodeSurface } from '@/components/nodes/shared';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { pixelSizeToAspectRatio, preloadImageAndGetSize, getNaturalSizeFromImgEl, simplifyRatio, parseWxH } from '@flowmuse/models/shared/image-size';
import { readVideoMeta } from '@/lib/editor-url';
import { ReuploadButton } from '@/components/nodes/shared/reupload-button';
import { requestLibraryPick, type LibraryPickResult } from '@/components/canvas/utils/asset-folders-events';
import { SubjectPickerDialog } from '@/components/nodes/shared/subject-picker-dialog';
import { env } from '@/lib/env';
import type { UploadNodeProps } from '.';
import { useI18n } from '@/providers/i18n';
import { EditWithPinturaOnDoubleClick } from '@/components/nodes/image/pintura-edit-on-double-click';
import { PreviewImageOnClick } from '@/components/nodes/image/preview-on-click';
import { EditWithPinturaVideoOnDoubleClick } from '@/components/nodes/video/pintura-edit-on-double-click';
import { PreviewVideoOnClick } from '@/components/nodes/video/preview-on-click';
import { AudioPlayer } from '@/components/ui/audio-player';
import { useDropzone, type DropzoneOptions } from 'react-dropzone';
import { ImageTopToolbar } from '@/components/nodes/image/image-top-toolbar';
import { VideoTopToolbar } from '@/components/nodes/video/video-top-toolbar';
import { AudioTopToolbar } from '@/components/nodes/audio/audio-top-toolbar';
import { toast } from 'sonner';
import { nanoid } from 'nanoid';
import { useNodeOperations } from '@/providers/node-operations';
import { DescriptionDialog } from '@/components/nodes/image/description-dialog';
import { useExtractFrame } from '@/hooks/use-extract-frame';
import { isManagedImageUrl, isPregeneratedThumbnailUrl, useImageVariant } from '@/lib/image-variant';
import { UploadCollapsedSummary } from '@/components/nodes/upload/collapsed-summary';
import { GlbViewer } from '@/components/nodes/model/glb-viewer';
import { isAssetRefUrl, resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';
import { selectNodeSelectedById } from '../store-selectors';

// Avoid spamming poster generation requests across nodes/rerenders in a single session.
const __uploadVideoPosterTriggeredUrls = new Set<string>();

const CANVAS_IMAGE_TRANSFORM = { height: 512, resize: 'contain', quality: 75 } as const;

const EMPTY_MENU_ITEM_CN =
  'flex items-center gap-4 px-4 py-3.5 text-sm text-foreground/80 whitespace-nowrap hover:bg-sidebar-accent/40 active:bg-sidebar-accent/60 transition-colors rounded-xl';

type UploadPrimitiveProps = UploadNodeProps & {
  title: string;
};

// 检测文件类型
function detectFileType(mimeType: string): 'image' | 'video' | 'audio' | 'model' {
  const mt = String(mimeType || '').toLowerCase();
  if (mt.startsWith('image/')) return 'image';
  if (mt.startsWith('video/')) return 'video';
  if (mt.startsWith('audio/')) return 'audio';
  if (mt.startsWith('model/')) return 'model';
  if (mt.includes('gltf')) return 'model';
  // 默认
  return 'image';
}

async function buildUploadUrlMetaPatch(params: {
  url: string;
  fileType?: 'image' | 'video' | 'audio' | 'model';
  existingData?: any;
}): Promise<Record<string, any>> {
  const { url, fileType, existingData } = params;
  const patch: Record<string, any> = {};
  if (!url) return patch;
  try {
    if (fileType === 'image') {
      const cur = existingData?.size as string | undefined;
      if (cur && cur !== 'match') return patch;
      const sz = await preloadImageAndGetSize(url);
      if (sz) patch.size = sz;
      return patch;
    }
    if (fileType === 'video') {
      // 真实分辨率统一存于 size（"WxH"），与 image 一致；兼容旧数据的 width/height。
      const curSize = typeof existingData?.size === 'string' ? existingData.size.trim() : '';
      const hasSize = /^\d+x\d+$/i.test(curSize);
      const hasDur =
        Number.isFinite(Number(existingData?.durationSeconds)) &&
        Number(existingData?.durationSeconds) > 0;

      // If we already have everything needed for sizing, skip reading meta.
      if (hasSize && hasDur) return patch;

      const meta = await readVideoMeta(url);
      const width = Number((meta as any)?.width);
      const height = Number((meta as any)?.height);
      const duration = Number((meta as any)?.durationInSeconds);

      if (!hasSize && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        patch.size = `${width}x${height}`;
      }
      if (!hasDur && Number.isFinite(duration) && duration > 0) {
        patch.durationSeconds = Math.round(duration * 100) / 100;
      }
      if (typeof (meta as any)?.hasAudioTrack === 'boolean') {
        patch.hasAudioTrack = (meta as any).hasAudioTrack;
      }
      return patch;
    }
  } catch {}
  return patch;
}

export const UploadPrimitive = ({
  data,
  id,
  type,
  title,
}: UploadPrimitiveProps) => {
  const { t } = useI18n();
  const { updateNodeData, getNode, addEdges, setNodes } = useReactFlow();
  const { addNode } = useNodeOperations();
  const project = useProject();
  const [isUploading, setIsUploading] = useState(false);
  const { getToken } = useAuth();
  const { user } = useUser();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [modelZoomOpen, setModelZoomOpen] = useState(false);
  const contentUrl = data.content?.url as string | undefined;
  const isAssetRef = isAssetRefUrl(contentUrl);
  const assetPreviewUrl = (data as any)?.assetPreviewUrl as string | undefined;
  const displayUrl = resolveAssetRefPreview(contentUrl, assetPreviewUrl);
  const fileType = useMemo(() => {
    const explicit = (data as any)?.fileType as any;
    if (explicit) return explicit;
    try {
      const name = String((data as any)?.content?.name || '');
      const url = String(contentUrl || '');
      if (/\.(glb|gltf)(\?|#|$)/i.test(name) || /\.(glb|gltf)(\?|#|$)/i.test(url)) return 'model';
    } catch {}
    return (data as any)?.content?.type ? detectFileType((data as any).content.type) : undefined;
  }, [data.fileType, (data as any)?.content?.type, (data as any)?.content?.name, contentUrl]);
  const isCanvasImage = Boolean(displayUrl) && fileType !== 'video' && fileType !== 'audio' && fileType !== 'model';
  // 优先 media 服务预生成的缩略图（OSS 图走 OSS 图片处理），未生成时 onError 回退原图
  const { src: canvasImageUrl, onError: onCanvasImageError } = useImageVariant(
    isCanvasImage ? displayUrl : undefined,
    CANVAS_IMAGE_TRANSFORM,
  );
  // 画布图经缩略图 / OSS 处理或 Next 图片优化显示时，<img> 的 naturalWidth/Height 不是原图尺寸，
  // 尺寸改由预加载原图获取。Supabase / OSS 地址即使回退到原图也经 Next 优化（缩小），同样不可信。
  const isCanvasImageTransformed = Boolean(
    displayUrl && canvasImageUrl && (canvasImageUrl !== displayUrl || isManagedImageUrl(displayUrl))
  );
  // 主体库 multi 模式：节点封面是首图，subjectImages 里保留全部原图
  // 用于在节点上显示"图组 ×N"角标，并将单击预览升级为多图浏览
  const subjectImages = useMemo<string[] | undefined>(() => {
    const arr = (data as any)?.subjectImages;
    if (!Array.isArray(arr)) return undefined;
    const list = arr.map((u: unknown) => String(u || '').trim()).filter(Boolean);
    return list.length > 1 ? list : undefined;
  }, [(data as any)?.subjectImages]);
  const isSubjectGroup = Boolean(subjectImages && subjectImages.length > 1 && fileType === 'image');
  const forcedUploading = Boolean((data as any)?.uploading);
  const effectiveUploading = Boolean(isUploading || forcedUploading);
  const [editingAssetNote, setEditingAssetNote] = useState('');
  const [isDescribing, setIsDescribing] = useState(false);
  const [showDescDialog, setShowDescDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const emptyFileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { extractToImageNodeAt } = useExtractFrame();
  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const isSelected = useStore(selectIsSelected);

  useEffect(() => {
    setEditingAssetNote(String((data as any)?.assetNote || ''));
  }, [(data as any)?.assetNote]);

  // Backfill: previously-uploaded video nodes should also trigger rendi poster generation once.
  useEffect(() => {
    if (fileType !== 'video') return;
    if (!contentUrl || isAssetRef) return;
    if (__uploadVideoPosterTriggeredUrls.has(contentUrl)) return;
    __uploadVideoPosterTriggeredUrls.add(contentUrl);
    (async () => {
      try {
        await fetch('/api/video/posters', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUrl: contentUrl }),
        }).then((r) => (r.ok ? r.json().catch(() => null) : null)).catch(() => null);
      } catch {}
    })();
  }, [fileType, contentUrl]);

  // When node is created from existing URLs (e.g. drag from asset library), we may not have size/meta yet.
  // Populate image natural size / video meta so NodeLayout aspectRatio can auto-fit the node container.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const urlToProbe = isAssetRef ? assetPreviewUrl : contentUrl;
        if (!urlToProbe) return;
        const patch = await buildUploadUrlMetaPatch({
          url: urlToProbe,
          fileType,
          existingData: data,
        });
        if (cancelled) return;
        if (patch && Object.keys(patch).length) updateNodeData(id, patch);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [
    contentUrl,
    assetPreviewUrl,
    isAssetRef,
    fileType,
    id,
    updateNodeData,
    (data as any)?.size,
    (data as any)?.width,
    (data as any)?.height,
    (data as any)?.aspectRatio,
    (data as any)?.durationSeconds,
  ]);

  // 监听来自 ImageTopToolbar 的图片更新事件
  useEffect(() => {
    const onUpdated = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const url = e?.detail?.url as string | undefined;
        const type = e?.detail?.type as string | undefined;
        const width = Number(e?.detail?.width);
        const height = Number(e?.detail?.height);
        const sizeStr = e?.detail?.size as string | undefined;
        if (!url) return;
        const patch: any = {
          content: { url, type: type || 'image/png' },
          sourceUrl: url,
          updatedAt: new Date().toISOString(),
        };
        const sz =
          (typeof sizeStr === 'string' && /^\d+x\d+$/i.test(sizeStr) ? sizeStr : undefined) ||
          (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? `${width}x${height}` : undefined);
        if (sz) patch.size = sz;
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
          patch.width = width;
          patch.height = height;
        }
        updateNodeData(id, patch);
      } catch {}
    };
    window.addEventListener('image_node_updated', onUpdated);
    return () => window.removeEventListener('image_node_updated', onUpdated);
  }, [id, updateNodeData]);

  // 顶部工具栏“预览”按钮事件（图片上传节点）
  useEffect(() => {
    const onOpenPreview = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        setPreviewOpen(true);
      } catch {}
    };
    window.addEventListener('open_image_preview', onOpenPreview as any);
    return () => window.removeEventListener('open_image_preview', onOpenPreview as any);
  }, [id]);

  // 同步滑块 -> 节点内视频进度（视频上传场景）
  useEffect(() => {
    const onSeek = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const sec = Number(e?.detail?.seconds);
        if (!Number.isFinite(sec) || sec < 0) return;
        const v = videoRef.current;
        if (!v) return;
        try { v.currentTime = Math.max(0, sec); } catch {}
      } catch {}
    };
    const onPause = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const v = videoRef.current;
        if (!v) return;
        try { v.pause(); } catch {}
      } catch {}
    };
    const onPlay = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const v = videoRef.current;
        if (!v) return;
        try { v.play().catch(() => {}); } catch {}
      } catch {}
    };
    window.addEventListener('video_seek_to', onSeek as any);
    window.addEventListener('video_pause', onPause as any);
    window.addEventListener('video_play', onPlay as any);
    return () => {
      window.removeEventListener('video_seek_to', onSeek as any);
      window.removeEventListener('video_pause', onPause as any);
      window.removeEventListener('video_play', onPlay as any);
    };
  }, [id]);

  // 仅在选中上传节点时播放视频，未选中则暂停并重置到开头
  useEffect(() => {
    if (fileType !== 'video') return;
    const v = videoRef.current;
    if (!v) return;
    try {
      if (isSelected) {
        v.muted = true;
        v.play().catch(() => {});
      } else {
        v.pause();
        v.currentTime = 0;
      }
    } catch {}
  }, [fileType, isSelected]);

  // 打开预览（视频上传场景）
  useEffect(() => {
    const onOpenPreview = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        setPreviewOpen(true);
      } catch {}
    };
    window.addEventListener('open_video_preview', onOpenPreview as any);
    return () => window.removeEventListener('open_video_preview', onOpenPreview as any);
  }, [id]);

  const handleDrop = async (files: File[]) => {
    if (effectiveUploading || !project?.id) {
      return;
    }

    try {
      if (!files.length) {
        throw new Error('No file selected');
      }

      setIsUploading(true);
      const [file] = files;
      const inferredAssetType = (() => {
        try {
          const mt = String(file?.type || '').toLowerCase();
          if (mt.startsWith('image/')) return 'image' as const;
          if (mt.startsWith('video/')) return 'video' as const;
          if (mt.startsWith('audio/')) return 'audio' as const;
          // 3D model uploads should be stored under "file"
          const name = String(file?.name || '').toLowerCase();
          if (mt.startsWith('model/') || mt.includes('gltf') || name.endsWith('.glb') || name.endsWith('.gltf')) return 'file' as const;
          return undefined;
        } catch {
          return undefined;
        }
      })();
      const token = await getToken({ template: 'supabase' });
      if (!token) throw new Error('No Clerk token');
      if (!user?.id) throw new Error('No Clerk user');
      const { url, type: mimeType } = await uploadFile(file, 'files', {
        token,
        userId: user.id,
        projectId: String(project.id),
        assetType: inferredAssetType,
      });
      const detectedType = (() => {
        try {
          const name = String(file?.name || '').toLowerCase();
          if (name.endsWith('.glb') || name.endsWith('.gltf')) return 'model' as const;
        } catch {}
        return detectFileType(mimeType);
      })();

      const updateData: any = {
        content: {
          url,
          type: mimeType,
          name: file.name,
        },
        fileType: detectedType,
      };

      // 根据文件类型执行特定操作
      if (detectedType === 'image') {
        // 统一的 URL 元数据补齐：size
        try {
          Object.assign(
            updateData,
            await buildUploadUrlMetaPatch({ url, fileType: detectedType, existingData: updateData })
          );
        } catch {}

        // 自动描述图片
        if ((env.NEXT_PUBLIC_IMAGE_PRIMITIVE_AUTO_DESCRIBE_ENABLED ?? '').toLowerCase() === 'true') {
          try {
            const description = await describeAction(url, project?.id);
            if ('error' in description) {
              throw new Error(description.error);
            }
            updateData.description = description.description;
          } catch {}
        }
      } else if (detectedType === 'audio') {
        // 获取音频时长
        const durationSeconds = await new Promise<number>((resolve) => {
          try {
            const el = new Audio();
            el.preload = 'metadata';
            el.src = url;
            const onLoaded = () => {
              resolve(Number.isFinite(el.duration) ? el.duration : 0);
              cleanup();
            };
            const onError = () => { resolve(0); cleanup(); };
            const cleanup = () => {
              el.removeEventListener('loadedmetadata', onLoaded);
              el.removeEventListener('error', onError);
            };
            el.addEventListener('loadedmetadata', onLoaded);
            el.addEventListener('error', onError);
          } catch {
            resolve(0);
          }
        });
        if (durationSeconds && Number.isFinite(durationSeconds)) {
          updateData.durationSeconds = durationSeconds;
        }

        // 音频转写
        try {
          const response = await transcribeAction(url, project?.id);
          if (!('error' in response)) {
            updateData.transcript = response.transcript;
          }
        } catch {}
      } else if (detectedType === 'video') {
        // 统一的 URL 元数据补齐：width/height/aspectRatio/durationSeconds
        try {
          Object.assign(
            updateData,
            await buildUploadUrlMetaPatch({ url, fileType: detectedType, existingData: updateData })
          );
        } catch {}
      } else if (detectedType === 'model') {
        // 3D models currently don't need extra meta for layout sizing.
      }

      updateNodeData(id, updateData);

      // 上传视频后：使用 rendi 生成并保存首/尾帧（_first.jpg / _last.jpg）
      if (detectedType === 'video' && url) {
        (async () => {
          try {
            const resp = await fetch('/api/video/posters', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ videoUrl: url }),
            });
            // 不需要写回节点：首/尾帧 URL 是规则（<base>_first.jpg / <base>_last.jpg）
            // 这里只负责触发服务端生成并落盘
            try { if (resp.ok) await resp.json().catch(() => null); } catch {}
          } catch {}
        })();
      }
    } catch (error) {
      handleError(t('Error uploading file'), error);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePickFromLibrary = async (item: LibraryPickResult) => {
    const url = item.rawSrc ?? item.src;
    if (!url) return;
    const detectedType = item.type || 'image';
    const mimeType =
      item.contentType ||
      (detectedType === 'video'
        ? 'video/mp4'
        : detectedType === 'audio'
          ? 'audio/mpeg'
          : 'image/png');

    const updateData: any = {
      content: {
        url,
        type: mimeType,
        name: item.name || undefined,
      },
      fileType: detectedType,
      sourceUrl: url,
    };

    try {
      Object.assign(
        updateData,
        await buildUploadUrlMetaPatch({
          url,
          fileType: detectedType,
          existingData: updateData,
        }),
      );
    } catch {}

    updateNodeData(id, updateData);
  };

  const openLibraryPicker = () => {
    void requestLibraryPick({ fileType }).then((item) => {
      if (item) void handlePickFromLibrary(item);
    });
  };

  const handlePickFromSubjectLibrary = useCallback((item: {
    id: string;
    name: string;
    image?: string | null;
    uploadedImage?: string[] | null;
    prompt?: string | null;
    mode?: 'mosaic' | 'multi';
  }) => {
    try {
      const mode = item.mode === 'multi' ? 'multi' : 'mosaic';
      const images = Array.isArray(item.uploadedImage)
        ? (item.uploadedImage as string[]).map((u) => String(u || '').trim()).filter(Boolean)
        : [];
      // multi 模式封面用首图；mosaic 模式用合成的 image 字段
      const coverUrl =
        mode === 'multi' && images.length > 0
          ? images[0]
          : String(item.image || '').trim() || (images[0] ?? '');
      if (!coverUrl) return;
      const subjectName = String(item.name || '').trim() || t('Subject');
      const promptText = String(item.prompt || '').trim();
      const updateData: Record<string, any> = {
        name: t('Subject: {{name}}', { name: subjectName }).slice(0, 64),
        content: { url: coverUrl, type: 'image/png' },
        fileType: 'image',
        sourceUrl: coverUrl,
        subject: true,
        subjectMode: mode,
        ...(promptText ? { prompt: promptText } : {}),
        ...(mode === 'multi' && images.length > 1 ? { subjectImages: images } : {}),
      };
      updateNodeData(id, updateData);
    } catch (e) {
      handleError(t('Pick from subject library failed'), e);
    }
  }, [id, updateNodeData, t]);

  const handleConnectNode = (nodeType: 'image' | 'video') => {
    const nodeRef = getNode(id) as any;
    if (!nodeRef) return;
    const baseX = nodeRef?.position?.x ?? 0;
    const baseY = nodeRef?.position?.y ?? 0;
    const width = (nodeRef?.measured?.width ?? 300) as number;
    const gap = 120;
    const position = { x: baseX + width + gap, y: baseY };

    const newId = addNode(nodeType, {
      position,
      selected: false,
      data: {},
    });
    if (!newId) return;

    requestAnimationFrame(() => {
      addEdges({
        id: nanoid(),
        source: id,
        target: newId,
        type: 'animated',
      } as any);
    });
  };

  const handlePasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((mt) => mt.startsWith('image/'));
        if (!imageType) continue;
        const blob = await item.getType(imageType);
        const ext = imageType.split('/')[1] || 'png';
        const file = new File([blob], `clipboard.${ext}`, { type: imageType });
        void handleDrop([file]);
        return;
      }
      toast.error(t('No image in clipboard'));
    } catch {
      toast.error(t('No image in clipboard'));
    }
  };

  // 根据文件类型确定 accept 和 subtitle
  const acceptConfig = useMemo(() => {
    if (fileType === 'image') {
      return {
        accept: { 'image/png': [], 'image/jpeg': [], 'image/webp': [] } as DropzoneOptions['accept'],
        subtitle: 'PNG/JPG/WebP • 1KB–20MB',
        maxSize: 1024 * 1024 * 20,
      };
    } else if (fileType === 'video') {
      return {
        accept: { 'video/mp4': [] } as DropzoneOptions['accept'],
        subtitle: 'MP4 • 1KB–200MB',
        maxSize: 1024 * 1024 * 200,
      };
    } else if (fileType === 'audio') {
      return {
        accept: { 'audio/*': [] } as DropzoneOptions['accept'],
        subtitle: 'Audio • 1KB–10MB',
        maxSize: 1024 * 1024 * 10,
      };
    } else if (fileType === 'model') {
      return {
        accept: {
          'model/gltf-binary': ['.glb'],
          'application/octet-stream': ['.glb'],
        } as DropzoneOptions['accept'],
        subtitle: 'GLB • 1KB–200MB',
        maxSize: 1024 * 1024 * 200,
      };
    }
    // 默认：支持所有类型
    return {
      accept: { 'image/*': [], 'video/*': [], 'audio/*': [], 'model/*': [] } as DropzoneOptions['accept'],
      subtitle: 'Image/Video/Audio/3D • 1KB–200MB',
      maxSize: 1024 * 1024 * 200,
    };
  }, [fileType]);

  const emptyDropzone = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: acceptConfig.maxSize,
    minSize: 1024,
    maxFiles: 1,
    multiple: false,
    accept: acceptConfig.accept,
    disabled: effectiveUploading,
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0) {
        handleError(t('Upload failed'), new Error(rejected[0]?.errors?.[0]?.message));
        return;
      }
      handleDrop(accepted);
    },
  });

  const toolbar: React.ComponentProps<typeof NodeLayout>['toolbar'] = contentUrl
    ? [
        {
          children: (
            <div className="flex items-center gap-2">
              <ReuploadButton
                disabled={effectiveUploading}
                onSelectFiles={(files) => void handleDrop(files)}
                accept={
                  fileType === 'image'
                    ? 'image/png,image/jpeg,image/webp'
                    : fileType === 'video'
                      ? 'video/mp4'
                      : fileType === 'audio'
                        ? 'audio/*'
                        : '.glb,model/gltf-binary'
                }
                aria-label={t('Re-upload')}
              />
              {fileType !== 'model' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-xl h-9 px-4 bg-sidebar-accent/50 hover:bg-sidebar-accent transition-all font-bold text-xs gap-2 border border-sidebar-border/30 shadow-sm active:scale-95"
                  disabled={effectiveUploading}
                  onClick={openLibraryPicker}
                >
                  <FolderOpen size={14} className="text-primary" />
                  <span>{t('Pick from library')}</span>
                </Button>
              )}
            </div>
          ),
        },
      ]
    : undefined;

  // 根据文件类型确定显示比例
  const aspectRatio = useMemo(() => {
    if (fileType === 'image') {
      return pixelSizeToAspectRatio((data as any)?.size as any, '1/1');
    } else if (fileType === 'video') {
      // 真实分辨率统一存于 size（"WxH"）；兼容旧数据的 width/height 与 aspectRatio（含历史上的 "WxH" 写法）。
      const fromSize = parseWxH((data as any)?.size);
      if (fromSize) return simplifyRatio(fromSize.width, fromSize.height).replace(':', '/');

      const w = Number((data as any)?.width);
      const h = Number((data as any)?.height);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        return simplifyRatio(w, h).replace(':', '/');
      }

      const fromAr = parseWxH((data as any)?.aspectRatio);
      if (fromAr) return simplifyRatio(fromAr.width, fromAr.height).replace(':', '/');

      const ar = String((data as any)?.aspectRatio || '').trim();
      if (/^\d+:\d+$/.test(ar)) {
        const [arW, arH] = ar.split(':').map(Number);
        if (Number.isFinite(arW) && Number.isFinite(arH) && arW > 0 && arH > 0) {
          return simplifyRatio(arW, arH).replace(':', '/');
        }
        return ar.replace(':', '/');
      }
      return '1/1';
    } else if (fileType === 'audio') {
      return '1/1';
    } else if (fileType === 'model') {
      return '1/1';
    }
    return '1/1'; // 默认
  }, [fileType, (data as any)?.size, (data as any)?.width, (data as any)?.height, (data as any)?.aspectRatio]);

  // 渲染内容预览
  const renderContent = () => {
    if (effectiveUploading) {
      return (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-sidebar-accent/10" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-2xl bg-sidebar/80 backdrop-blur-xl border border-sidebar-border shadow-none">
              <Loader2Icon size={20} className="animate-spin text-primary" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-foreground/70">
                {t('Uploading...')}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (!contentUrl) {
      return (
        <div className="absolute inset-0" {...emptyDropzone.getRootProps()}>
          <input {...emptyDropzone.getInputProps()} className="hidden" />
          <input
            ref={emptyFileInputRef}
            type="file"
            accept={
              fileType === 'image'
                ? 'image/png,image/jpeg,image/webp'
                : fileType === 'video'
                  ? 'video/mp4'
                  : fileType === 'audio'
                    ? 'audio/*'
                    : fileType === 'model'
                      ? '.glb,model/gltf-binary'
                      : 'image/*,video/*,audio/*'
            }
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files ? Array.from(e.target.files) : [];
              if (picked.length) void handleDrop(picked);
              if (emptyFileInputRef.current) emptyFileInputRef.current.value = '';
            }}
          />

          {emptyDropzone.isDragActive ? (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 transition-colors">
              <div className="flex flex-col items-center gap-2">
                <Upload size={22} className="text-primary" />
                <span className="text-sm font-medium text-primary">{t('Upload')}</span>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="flex flex-col w-fit max-w-full">
                <button
                  type="button"
                  className={EMPTY_MENU_ITEM_CN}
                  onClick={() => emptyFileInputRef.current?.click()}
                >
                  <Upload size={18} className="text-muted-foreground/70 shrink-0" />
                  <span>{t('Upload from local')}</span>
                </button>
                {fileType !== 'video' && fileType !== 'audio' && (
                  <button
                    type="button"
                    className={EMPTY_MENU_ITEM_CN}
                    onClick={() => void handlePasteFromClipboard()}
                  >
                    <ClipboardPaste size={18} className="text-muted-foreground/70 shrink-0" />
                    <span>{t('Paste from clipboard')}</span>
                  </button>
                )}
                {fileType !== 'model' && (
                  <button
                    type="button"
                    className={EMPTY_MENU_ITEM_CN}
                    onClick={openLibraryPicker}
                  >
                    <FolderOpen size={18} className="text-muted-foreground/70 shrink-0" />
                    <span>{t('Pick from library')}</span>
                  </button>
                )}
                {fileType !== 'video' && fileType !== 'audio' && fileType !== 'model' && (
                  <button
                    type="button"
                    className={EMPTY_MENU_ITEM_CN}
                    onClick={() => setSubjectPickerOpen(true)}
                  >
                    <UserSquare size={18} className="text-muted-foreground/70 shrink-0" />
                    <span>{t('Pick from subject library')}</span>
                  </button>
                )}
                {fileType !== 'video' && fileType !== 'audio' && (
                  <>
                    <div className="mx-4 my-1 h-px bg-border/40" />
                    <button
                      type="button"
                      className={EMPTY_MENU_ITEM_CN}
                      onClick={() => handleConnectNode('image')}
                    >
                      <ImagePlus size={18} className="text-muted-foreground/70 shrink-0" />
                      <span>{t('Connect image node')}</span>
                    </button>
                    <button
                      type="button"
                      className={EMPTY_MENU_ITEM_CN}
                      onClick={() => handleConnectNode('video')}
                    >
                      <Film size={18} className="text-muted-foreground/70 shrink-0" />
                      <span>{t('Connect video node')}</span>
                    </button>
                  </>
                )}
                {fileType === 'video' && (
                  <>
                    <div className="mx-4 my-1 h-px bg-border/40" />
                    <button
                      type="button"
                      className={EMPTY_MENU_ITEM_CN}
                      onClick={() => handleConnectNode('video')}
                    >
                      <Film size={18} className="text-muted-foreground/70 shrink-0" />
                      <span>{t('Connect video node')}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (isAssetRef && assetPreviewUrl) {
      if (fileType === 'video') {
        return (
          <>
            <PreviewVideoOnClick
              className="absolute inset-0"
              onPreview={() => setPreviewOpen(true)}
              enabled={isSelected}
            >
              <video
                ref={videoRef}
                src={assetPreviewUrl}
                className="w-full h-full object-cover cursor-zoom-in"
                muted
                loop
                playsInline
                onLoadedMetadata={() => {
                  try {
                    const v = videoRef.current as HTMLVideoElement | null;
                    const vw = Number(v?.videoWidth ?? NaN);
                    const vh = Number(v?.videoHeight ?? NaN);
                    if (Number.isFinite(vw) && Number.isFinite(vh) && vw > 0 && vh > 0) {
                      // 真实分辨率统一存于 size（"WxH"），与 image 一致。
                      const sizeStr = `${vw}x${vh}`;
                      if (String((data as any)?.size || '').trim() !== sizeStr) {
                        updateNodeData(id, { size: sizeStr });
                      }
                    }
                    if (v && isSelected) {
                      try { v.muted = true; v.play().catch(() => {}); } catch {}
                    }
                  } catch {}
                }}
              />
            </PreviewVideoOnClick>
            {!isSelected && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/45">
                  <PlayIcon size={26} className="text-white opacity-90" />
                </div>
              </div>
            )}
          </>
        );
      }
      if (fileType === 'audio') {
        return (
          <div className="relative z-10 flex h-full items-center justify-center p-4">
            <div className="w-full max-w-[560px]">
              <AudioPlayer src={assetPreviewUrl} className="w-full" />
            </div>
          </div>
        );
      }
      return (
        <PreviewImageOnClick
          className="absolute inset-0"
          onPreview={() => setPreviewOpen(true)}
          enabled={isSelected}
        >
          <Image
            src={canvasImageUrl || assetPreviewUrl}
            // 预生成缩略图直接加载（已足够小，且让其缓存头生效）；其余仍经 Next 图片优化
            unoptimized={isPregeneratedThumbnailUrl(canvasImageUrl || assetPreviewUrl || '')}
            onError={onCanvasImageError}
            alt={t('Image')}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority={false}
            loading="lazy"
            onLoad={(e) => {
              try {
                if (isCanvasImageTransformed) return;
                const sz = getNaturalSizeFromImgEl(e.currentTarget);
                const cur = (data as any)?.size as string | undefined;
                if (sz && (!cur || cur === 'match')) updateNodeData(id, { size: sz });
              } catch {}
            }}
            className="object-cover cursor-zoom-in"
          />
        </PreviewImageOnClick>
      );
    }

    // Asset:// 引用但尚未解析到预览 URL：显示素材 ID 卡片
    if (isAssetRef) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-sidebar-accent/10">
          <div className="flex flex-col items-center gap-2.5 px-6 max-w-full w-full">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center ring-1 ring-primary/20">
              <Package size={18} className="text-primary" />
            </div>
            <input
              type="text"
              className="w-full max-w-[200px] h-7 px-2 text-center text-sm font-semibold bg-transparent border-b border-transparent hover:border-sidebar-border/50 focus:border-primary/40 outline-none transition-colors placeholder:text-muted-foreground/40 placeholder:font-normal placeholder:text-xs text-foreground/90"
              placeholder={t('Add note')}
              value={editingAssetNote}
              onChange={(e) => setEditingAssetNote(e.target.value)}
              onBlur={() => updateNodeData(id, { assetNote: editingAssetNote })}
            />
            <div className="max-w-full px-3 py-1.5 rounded-lg bg-sidebar-accent/40 border border-sidebar-border/50 text-xs font-mono text-foreground/60 truncate">
              {contentUrl!.replace('Asset://', '')}
            </div>
          </div>
        </div>
      );
    }

    // 根据文件类型渲染不同的预览
    if (fileType === 'image') {
      return (
        <PreviewImageOnClick
          className="absolute inset-0"
          onPreview={() => setPreviewOpen(true)}
          enabled={isSelected}
        >
          <EditWithPinturaOnDoubleClick
            src={contentUrl}
            className="absolute inset-0"
            onUploadingChange={(u) => setIsUploading(u)}
            onUpdated={({ url, type, width, height, size }) => {
              const patch: any = {
                content: { url, type },
                sourceUrl: url,
                updatedAt: new Date().toISOString(),
              };
              const sz =
                (typeof size === 'string' && /^\d+x\d+$/i.test(size) ? size : undefined) ||
                (Number.isFinite(width) && Number.isFinite(height) && (width as number) > 0 && (height as number) > 0
                  ? `${width}x${height}`
                  : undefined);
              if (sz) patch.size = sz;
              if (Number.isFinite(width) && Number.isFinite(height) && (width as number) > 0 && (height as number) > 0) {
                patch.width = width;
                patch.height = height;
              }
              updateNodeData(id, patch);
            }}
          >
            <Image
              src={canvasImageUrl || contentUrl}
              unoptimized={isPregeneratedThumbnailUrl(canvasImageUrl || contentUrl || '')}
              onError={onCanvasImageError}
              alt={t('Image')}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              priority={false}
              loading="lazy"
              onLoad={(e) => {
                try {
                  // If canvas is using transformed URL, naturalWidth/Height reflect resized output.
                  // Don't persist resized dimensions into node state.
                  if (isCanvasImageTransformed) return;
                  const sz = getNaturalSizeFromImgEl(e.currentTarget);
                  const cur = (data as any)?.size as string | undefined;
                  if (sz && (!cur || cur === 'match')) updateNodeData(id, { size: sz });
                } catch {}
              }}
              className="object-cover cursor-zoom-in"
            />
          </EditWithPinturaOnDoubleClick>
        </PreviewImageOnClick>
      );
    } else if (fileType === 'video') {
      return (
        <>
          <PreviewVideoOnClick
            className="absolute inset-0"
            onPreview={() => setPreviewOpen(true)}
            enabled={isSelected}
          >
            <EditWithPinturaVideoOnDoubleClick
              className="absolute inset-0"
              src={contentUrl}
              onUpdated={({ url, type, width, height, durationSeconds }) => {
                const patch: any = { content: { url, type }, sourceUrl: url, updatedAt: new Date().toISOString() };
                // 真实分辨率统一存于 size（"WxH"），与 image 一致。
                if (Number.isFinite(width) && Number.isFinite(height) && (width as number) > 0 && (height as number) > 0) {
                  patch.size = `${width}x${height}`;
                }
                if (Number.isFinite(durationSeconds) && (durationSeconds as number) > 0) patch.durationSeconds = durationSeconds;
                updateNodeData(id, patch);
              }}
              onUploadingChange={(u) => setIsUploading(u)}
            >
              <video
                ref={videoRef}
                src={contentUrl}
                className="w-full h-full object-cover cursor-zoom-in"
                muted
                loop
                playsInline
                onLoadedMetadata={() => {
                  try {
                    const v = videoRef.current as HTMLVideoElement | null;
                    const dur = Number(v?.duration ?? NaN);
                    const vw = Number(v?.videoWidth ?? NaN);
                    const vh = Number(v?.videoHeight ?? NaN);
                    if (Number.isFinite(dur) && dur > 0) {
                      const cur = Number((data as any)?.durationSeconds);
                      const rounded = Math.round(dur * 100) / 100;
                      if (!Number.isFinite(cur) || cur !== rounded) {
                        updateNodeData(id, {
                          durationSeconds: rounded,
                        });
                      }
                    }
                    if (Number.isFinite(vw) && Number.isFinite(vh) && vw > 0 && vh > 0) {
                      // 真实分辨率统一存于 size（"WxH"），与 image 一致。
                      const sizeStr = `${vw}x${vh}`;
                      if (String((data as any)?.size || '').trim() !== sizeStr) {
                        updateNodeData(id, { size: sizeStr });
                      }
                    }
                    // 自动播放仅在选中时触发
                    if (v && isSelected) {
                      try {
                        v.muted = true;
                        v.play().catch(() => {});
                      } catch {}
                    }
                  } catch {}
                }}
              />
            </EditWithPinturaVideoOnDoubleClick>
          </PreviewVideoOnClick>
          {/* 未选中上传节点时：只显示预览帧 + 圆形半透明播放按钮 */}
          {!isSelected && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/45">
                <PlayIcon size={26} className="text-white opacity-90" />
              </div>
            </div>
          )}
        </>
      );
    } else if (fileType === 'audio') {
      return (
        <div className="relative z-10 flex h-full items-center justify-center p-4">
          <div className="w-full max-w-[560px]">
            <AudioPlayer src={contentUrl} className="w-full" />
          </div>
        </div>
      );
    } else if (fileType === 'model') {
      return (
        <div
          className="absolute inset-0 bg-neutral-950 cursor-zoom-in"
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setModelZoomOpen(true);
          }}
          onWheel={(e) => {
            // Let OrbitControls zoom, but prevent canvas / reactflow from also zooming the viewport.
            e.stopPropagation();
          }}
          title={t('Double click to zoom') || 'Double click to zoom'}
        >
          <div className="absolute bottom-2 right-2 pointer-events-none z-10">
            <div className="text-xs sm:text-sm text-white/80 drop-shadow-none">
              {t('Double click to zoom')}
            </div>
          </div>
          <GlbViewer url={contentUrl} interactive={false} className="absolute inset-0" />
        </div>
      );
    }

    return null;
  };

  // 根据文件类型动态标题
  const computedTitle = useMemo(() => {
    if (fileType === 'image') return t('Uploaded image');
    if (fileType === 'video') return t('Uploaded video');
    if (fileType === 'audio') return t('Uploaded audio');
    if (fileType === 'model') return t('Uploaded 3D model');
    return t('Uploaded file');
  }, [fileType]);

  return (
    <>
    <NodeLayout
      id={id}
      data={data}
      type={type}
      title={computedTitle || title}
      variant="modern"
      aspectRatio={aspectRatio}
      collapsedAspectRatio="16/9"
      toolbar={toolbar}
      titleBubble={null}
      collapsedSummary={<UploadCollapsedSummary data={data as any} />}
    >
        {fileType === 'image' && contentUrl ? (
          <ImageTopToolbar
            nodeId={id}
            imageUrl={displayUrl}
            data={data}
            isToolbarBlocked={effectiveUploading || isDescribing}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isDescribing={isDescribing}
            hasDescription={Boolean((data as any)?.description)}
            onShowDescription={() => setShowDescDialog(true)}
            onParse={async () => {
              const parseUrl = displayUrl;
              if (!parseUrl || !project?.id) return;
              try {
                setIsDescribing(true);
                const res = await describeAction(parseUrl, project.id);
                if ('error' in res) throw new Error(res.error);
                updateNodeData(id, { description: res.description, updatedAt: new Date().toISOString() });
                setShowDescDialog(true);
              } catch (e) {
                handleError(t('Failed to parse image'), e);
              } finally {
                setIsDescribing(false);
              }
            }}
          />
        ) : null}
        {fileType === 'video' && displayUrl ? (
          <VideoTopToolbar
            nodeId={id}
            videoUrl={displayUrl}
            data={data}
            isToolbarBlocked={effectiveUploading}
          />
        ) : null}
        {fileType === 'audio' && displayUrl ? (
          <AudioTopToolbar
            nodeId={id}
            audioUrl={displayUrl}
            isToolbarBlocked={effectiveUploading}
          />
        ) : null}
        <NodeSurface 
          aspectRatio={aspectRatio} 
          bgStyle={{ backgroundColor: 'var(--node-bg)' }} 
          showDashed={false} 
          radiusClass="rounded-3xl"
          insetClass={contentUrl ? 'inset-0' : 'inset-1'}
        >
          {renderContent()}
          {(data as any)?.inpaintMask ? (
            <div className="absolute bottom-2 right-2 z-10 pointer-events-none">
              <div className="h-6 px-2 rounded-xl border border-primary/20 bg-primary/10 backdrop-blur-md text-[10px] font-bold tracking-wider text-primary flex items-center justify-center shadow-none ring-1 ring-white/10">
                {t('Mask exists')}
              </div>
            </div>
          ) : null}
          {isSubjectGroup ? (
            <button
              type="button"
              className="nodrag group/subject-group absolute top-3 right-3 z-10 inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-black/55 px-3.5 py-1.5 text-sm font-bold tracking-wide text-white shadow-lg ring-1 ring-white/10 backdrop-blur-md hover:bg-black/75 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPreviewOpen(true);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              title={t('View subject image group details')}
            >
              <Images size={16} />
              <span>{t('Subject image group')} ×{subjectImages!.length}</span>
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20 transition-colors group-hover/subject-group:bg-white/25">
                <Eye size={12} className="text-white" />
              </span>
            </button>
          ) : null}
        </NodeSurface>
      </NodeLayout>
      {fileType === 'image' && (
        <ImagePreviewDialog 
          open={previewOpen} 
          onOpenChange={setPreviewOpen} 
          {...(isSubjectGroup
            ? { urls: subjectImages, url: subjectImages?.[0] }
            : { url: displayUrl })}
          alt={t('Preview image')} 
          titleClose={t('Close preview')} 
        />
      )}
      {fileType === 'image' && (
        <DescriptionDialog
          open={showDescDialog}
          onOpenChange={setShowDescDialog}
          text={String((data as any)?.description || '')}
        />
      )}
      {fileType === 'video' && (
        <VideoPreviewDialog 
          open={previewOpen} 
          onOpenChange={setPreviewOpen} 
          url={displayUrl || ''} 
          titleClose={t('Close preview')}
          titleExtract={t('Extract current frame')}
          onExtractCurrentFrame={async (sec) => {
            if (!displayUrl) return;
            await extractToImageNodeAt(id, sec, displayUrl);
          }}
        />
      )}
      {fileType === 'model' && contentUrl ? (
        <Dialog open={modelZoomOpen} onOpenChange={setModelZoomOpen}>
          <DialogContent className="sm:max-w-4xl p-4">
            <DialogHeader className="pr-8">
              <DialogTitle>{t('3D Preview') || '3D Preview'}</DialogTitle>
            </DialogHeader>
            <div className="w-full h-[70vh] bg-neutral-950 rounded-xl overflow-hidden border border-neutral-800">
              <GlbViewer url={contentUrl} className="h-full w-full" />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
      <SubjectPickerDialog
        open={subjectPickerOpen}
        onOpenChange={setSubjectPickerOpen}
        onPick={handlePickFromSubjectLibrary}
      />
    </>
  );
};

